import { useAlert } from "@components/alert/withAlert";
import { getTableDrillDownLink } from "@components/drillDown/DrillDown.utils";
import SectionSwitch from "@components/splitWizard/SectionSwitch";
import { IColumn, IRow } from "@components/table";
import TableWithAutoSizedColumns from "@components/table/TableWithAutoSizedColumns";
import {
    EntitySetName,
    EntityTypeName,
    IPrTaxpayerDeclarationEntity,
    PrTaxpayerDeclarationEntity
} from "@odata/GeneratedEntityTypes";
import { PrTaxpayerDeclarationStatusCode } from "@odata/GeneratedEnums";
import { getEnumDisplayValue, getEnumNameSpaceName } from "@odata/GeneratedEnums.utils";
import { ISectionProps } from "@pages/payroll/payrollYearOperations/PayrollYearOperation.utils";
import {
    AlertWrapper,
    ClickableStyled,
    TableWrapper,
    Text
} from "@pages/payroll/payrollYearOperations/screens/PayrollYearOperationScreen.styles";
import { getThemeColorForDeclarationStatus } from "@pages/payroll/taxpayerDeclaration/TaxpayerDeclaration.def";
import { logger } from "@utils/log";
import React, { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { AppContext } from "../../../../contexts/appContext/AppContext.types";
import { ColoredText } from "../../../../global.style";
import { ROUTE_TAXPAYER_DECLARATIONS } from "../../../../routes";
import { getAlertFromError } from "../../../../views/formView/Form.utils";

const TaxPayerDeclaration: React.FC<ISectionProps> = ({
                                                          task,
                                                          year,
                                                          onCheck,
                                                          storage
                                                      }) => {
    const { t } = useTranslation(["PayrollYearOperation", getEnumNameSpaceName(EntityTypeName.PrTaxpayerDeclarationStatus)]);
    const { alert, setAlert } = useAlert({});
    const [rows, setRows] = React.useState<IRow[]>([]);
    const context = useContext(AppContext);

    const nextYear = year ? parseInt(year) + 1 : undefined; // https://solitea-cz.atlassian.net/browse/DEV-42570
    
    // load taxpayer declaration statuses count
    useEffect((): void => {
        try {
            const res = storage.oData.getEntitySetWrapper(EntitySetName.PrTaxpayerDeclarations)
                    .query()
                .filter(`${PrTaxpayerDeclarationEntity.Year} eq ${nextYear}`)
                    .groupBy(PrTaxpayerDeclarationEntity.StatusCode)
                    .aggregate("$count as @odata.count")
                    .fetchData<IPrTaxpayerDeclarationEntity[]>();

            res.then((response) => {
                const declaration = response.value;
                const rows = declaration.map((item) => {
                    const name = getEnumDisplayValue(EntityTypeName.PrTaxpayerDeclarationStatus, item.StatusCode);
                    return {
                        id: item.StatusCode,
                        values: {
                            [PrTaxpayerDeclarationEntity.StatusCode]: {
                                tooltip: name,
                                value: <ColoredText
                                        color={getThemeColorForDeclarationStatus(item.StatusCode as PrTaxpayerDeclarationStatusCode)}>
                                    {name}
                                </ColoredText>
                            },
                            count: getTableDrillDownLink((item._metadata[""].count ?? 0).toString(), {
                                route: ROUTE_TAXPAYER_DECLARATIONS,
                                context: context,
                                filters: {
                                    [PrTaxpayerDeclarationEntity.Status]: item.StatusCode,
                                    [PrTaxpayerDeclarationEntity.Year]: nextYear
                                }
                            })
                        }
                    };
                });
                setRows(rows);
            });
        } catch (e) {
            setAlert(getAlertFromError(e));
            logger.error("Error while fetching Taxpayer Declarations", e);
        }

    }, [year, storage.oData, t, context, setAlert]);

    const handleCheck = useCallback(async (checked: boolean) => {
        const alertProps = await onCheck(checked);
        setAlert(alertProps);
    }, [onCheck, setAlert]);

    const columns: IColumn[] = useMemo(() => {
        return [{
            id: PrTaxpayerDeclarationEntity.StatusCode,
            label: t("PayrollYearOperation:TaxPayerDeclaration.TaxPayerDeclarationStatusLabel"),
        }, {
            id: "count",
            label: t("PayrollYearOperation:Detail.Count"),
        }];
    }, [t]);

    return (<>
        <AlertWrapper>{alert}</AlertWrapper>
        <Text>{t("PayrollYearOperation:TaxPayerDeclaration.TaxPayerDeclarationStatus")}</Text>
        <TableWrapper>
            <TableWithAutoSizedColumns
                    rows={rows}
                    columns={columns}
                    disableSort={true}
                    disableVirtualization={true}/>
        </TableWrapper>
        <ClickableStyled link={ROUTE_TAXPAYER_DECLARATIONS}
                         isLink>{t("PayrollYearOperation:TaxPayerDeclaration.GoToTaxPayerDeclaration")}</ClickableStyled>
        <SectionSwitch
                isChecked={task.IsChecked}
                onCheck={handleCheck}/>
    </>);
};

export default TaxPayerDeclaration;